import * as React from "react"
import { ChevronDownIcon } from "lucide-react"
import { cn } from "@/lib/utils"

/**
 * shadcn radix-nova native Select primitive.
 *
 * Wraps the native `<select>` in a relative container that overlays a
 * `ChevronDownIcon` on the right edge. `appearance-none` strips the
 * browser-supplied picker chrome so the lucide chevron stays in lockstep
 * with the surrounding token resolution (text-muted-foreground + opacity).
 *
 * Native `<select>` is intentionally preferred over the @radix-ui/react-select
 * popover composition here: it keeps the existing `e.target.value` event
 * shape across all routes, leverages the OS-native picker on mobile, and
 * avoids a behavioural diff while still delivering the shadcn visual
 * baseline. If a future feature needs search/multi-select/asynchronous
 * option loading, swap this implementation for the Radix Select popover.
 */
const Select = React.forwardRef<
  HTMLSelectElement,
  React.ComponentPropsWithoutRef<"select">
>(({ className, children, ...props }, ref) => {
  return (
    <div className="relative">
      <select
        ref={ref}
        data-slot="select"
        className={cn(
          "flex h-9 w-full min-w-0 appearance-none rounded-lg border border-input bg-transparent px-3 py-1 pr-8 text-base transition-colors outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive/20 md:text-sm dark:bg-input/30 peer",
          className,
        )}
        {...props}
      >
        {children}
      </select>
      <ChevronDownIcon
        aria-hidden="true"
        className="pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground opacity-70"
      />
    </div>
  )
})
Select.displayName = "Select"

export { Select }
